import { useCallback, useEffect, useState } from "react";

import { authFetch } from "@/api/client";
import { useAuth, type Role } from "@/auth/AuthContext";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { KpiCard } from "@/components/KpiCard";
import { Pill } from "@/components/Pill";
import { useStore } from "@/state/store";

interface TreasuryView {
  usdc_balance: string;
  matic_balance: string;
  active_bonds_locked: string;
  pending_refunds_today: string;
  total_forfeited_lifetime: string;
  /** Campi opzionali esposti da estensioni future del backend. */
  total_proposed_lifetime?: string;
  total_settled_lifetime?: string;
  uma_rpc_url?: string;
  uma_oracle_address?: string;
  uma_collateral_address?: string;
  feature_flags?: Record<string, boolean>;
}

interface UserWithRole {
  id: string;
  email: string;
  country: string;
  kyc_tier: number;
  role: Role;
  created_at: string;
}

interface UsersResponse {
  items: UserWithRole[];
  next_cursor?: string | null;
}

type Tab = "treasury" | "system" | "operators";

/**
 * Pagina admin-only (la guard è già nel router).
 *
 * Organizza tre aree:
 *  1. Treasury — KPI di liquidità/operativi (USDC, MATIC, bond locked, ecc.);
 *  2. System — configurazione read-only (UMA endpoints, feature flags);
 *  3. Operators — sotto-lista degli utenti con ruolo `operation` o `admin`,
 *     con possibilità di promuovere un utente esistente.
 */
export function SettingsPage() {
  const [tab, setTab] = useState<Tab>("treasury");

  return (
    <div className="flex flex-col gap-4 max-w-[1400px]">
      <header>
        <h1 className="text-[22px] font-semibold">Impostazioni</h1>
        <p className="text-[13px] text-muted mt-1">
          Pagina riservata agli admin. Treasury, configurazione sistema e
          operatori.
        </p>
      </header>

      <nav className="flex gap-1 border-b border-line">
        <TabButton active={tab === "treasury"} onClick={() => setTab("treasury")}>
          Treasury
        </TabButton>
        <TabButton active={tab === "system"} onClick={() => setTab("system")}>
          System
        </TabButton>
        <TabButton
          active={tab === "operators"}
          onClick={() => setTab("operators")}
        >
          Operators
        </TabButton>
      </nav>

      {tab === "treasury" && <TreasuryPanel />}
      {tab === "system" && <SystemPanel />}
      {tab === "operators" && <OperatorsPanel />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-[1px] transition-colors ${
        active
          ? "border-accent text-text"
          : "border-transparent text-muted hover:text-text-2"
      }`}
    >
      {children}
    </button>
  );
}

/* ────────────────────────── TREASURY ───────────────────────── */

function TreasuryPanel() {
  const [treasury, setTreasury] = useState<TreasuryView | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await authFetch("/v1/admin/treasury");
      if (!res.ok) throw new Error(`treasury ${res.status}`);
      const data = (await res.json()) as TreasuryView;
      setTreasury(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "errore sconosciuto");
      setTreasury(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold">Treasury</h2>
        <button
          type="button"
          className="btn-secondary text-[12px] py-1 px-3"
          onClick={load}
          disabled={loading}
        >
          {loading ? "…" : "Refresh"}
        </button>
      </div>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento treasury fallito: {err}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="USDC balance"
          value={treasury ? treasury.usdc_balance : "—"}
          hint="Liquidità operativa"
        />
        <KpiCard
          label="MATIC balance"
          value={treasury ? treasury.matic_balance : "—"}
          hint="Gas reserve"
        />
        <KpiCard
          label="Bond locked"
          value={treasury ? treasury.active_bonds_locked : "—"}
          accent
          hint="UMA disputes attive"
        />
        <KpiCard
          label="Refund pending oggi"
          value={treasury ? treasury.pending_refunds_today : "—"}
          accent
          hint="Da processare"
        />
      </div>

      <div className="card">
        <h3 className="text-[14px] font-semibold mb-3">Lifetime stats</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-[13px]">
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted mb-1">
              Forfeited
            </dt>
            <dd className="font-mono text-text">
              {treasury?.total_forfeited_lifetime ?? "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted mb-1">
              Proposed
            </dt>
            <dd className="font-mono text-text">
              {treasury?.total_proposed_lifetime ?? "—"}
            </dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted mb-1">
              Settled
            </dt>
            <dd className="font-mono text-text">
              {treasury?.total_settled_lifetime ?? "—"}
            </dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

/* ────────────────────────── SYSTEM ───────────────────────── */

function SystemPanel() {
  const [treasury, setTreasury] = useState<TreasuryView | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Il backend espone le info di sistema dentro `/v1/admin/treasury` (campi
  // extension uma_*, feature_flags). Quando avremo un endpoint /system
  // dedicato sarà sufficiente spostare il fetch qui.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await authFetch("/v1/admin/treasury");
        if (!res.ok) throw new Error(`system ${res.status}`);
        const data = (await res.json()) as TreasuryView;
        if (!cancelled) setTreasury(data);
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : "errore sconosciuto");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <div className="card text-muted text-[13px]">Caricamento…</div>;
  }

  const flags = treasury?.feature_flags ?? {};

  return (
    <section className="flex flex-col gap-4">
      <h2 className="text-[16px] font-semibold">Configurazione sistema</h2>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento configurazione fallito: {err}
        </div>
      )}

      <div className="card">
        <h3 className="text-[14px] font-semibold mb-3">UMA Oracle</h3>
        <dl className="grid grid-cols-[180px,1fr] gap-y-2 text-[12.5px]">
          <dt className="text-muted">RPC URL</dt>
          <dd className="font-mono text-text-2 break-all">
            {treasury?.uma_rpc_url ? truncate(treasury.uma_rpc_url, 60) : "—"}
          </dd>
          <dt className="text-muted">Oracle address</dt>
          <dd className="font-mono text-text-2">
            {treasury?.uma_oracle_address ? (
              <PolygonscanLink address={treasury.uma_oracle_address} />
            ) : (
              "—"
            )}
          </dd>
          <dt className="text-muted">Collateral address</dt>
          <dd className="font-mono text-text-2">
            {treasury?.uma_collateral_address ? (
              <PolygonscanLink address={treasury.uma_collateral_address} />
            ) : (
              "—"
            )}
          </dd>
        </dl>
      </div>

      <div className="card">
        <h3 className="text-[14px] font-semibold mb-3">Feature flags</h3>
        {Object.keys(flags).length === 0 ? (
          <p className="text-[12.5px] text-muted">
            Nessun feature flag esposto dal backend.
          </p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(flags).map(([name, enabled]) => (
              <li
                key={name}
                className="flex items-center justify-between gap-3 rounded-md border border-line bg-bg-2 px-3 py-2 text-[12.5px]"
              >
                <span className="font-mono text-text-2">{name}</span>
                <Pill tone={enabled ? "yes" : "no"}>
                  {enabled ? "ON" : "OFF"}
                </Pill>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function PolygonscanLink({ address }: { address: string }) {
  return (
    <a
      href={`https://polygonscan.com/address/${address}`}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent-2 hover:underline break-all"
    >
      {address}
    </a>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/* ────────────────────────── OPERATORS ───────────────────────── */

function OperatorsPanel() {
  const auth = useAuth();
  const showToast = useStore((s) => s.showToast);

  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [promoteOpen, setPromoteOpen] = useState(false);
  const [demoting, setDemoting] = useState<UserWithRole | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await authFetch("/v1/admin/users?limit=200");
      if (!res.ok) throw new Error(`users ${res.status}`);
      const data = (await res.json()) as UsersResponse;
      setUsers(
        (data.items ?? []).filter(
          (u) => u.role === "operation" || u.role === "admin",
        ),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "errore sconosciuto");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns: DataTableColumn<UserWithRole>[] = [
    {
      key: "email",
      header: "Email",
      cell: (u) => <span className="text-text">{u.email}</span>,
      sortValue: (u) => u.email.toLowerCase(),
    },
    {
      key: "role",
      header: "Ruolo",
      cell: (u) => (
        <Pill tone={u.role === "admin" ? "warn" : "accent"}>{u.role}</Pill>
      ),
      sortValue: (u) => u.role,
    },
    {
      key: "created_at",
      header: "Registrato",
      cell: (u) => (
        <span className="font-mono text-[11.5px] text-muted">
          {new Date(u.created_at).toLocaleDateString("it-IT")}
        </span>
      ),
      sortValue: (u) => new Date(u.created_at),
    },
    {
      key: "actions",
      header: "Azioni",
      align: "right",
      cell: (u) => (
        <button
          type="button"
          className="btn-secondary py-1 px-3 text-[11.5px]"
          onClick={(e) => {
            e.stopPropagation();
            setDemoting(u);
          }}
          disabled={u.id === auth.userId}
          title={
            u.id === auth.userId ? "Non puoi demotare te stesso" : undefined
          }
        >
          Demota
        </button>
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold">Operatori</h2>
        <button
          type="button"
          className="btn-primary text-[12px]"
          onClick={() => setPromoteOpen(true)}
        >
          + Promuovi a operation
        </button>
      </div>

      {err && (
        <div className="card text-[12.5px] text-no">
          Caricamento operatori fallito: {err}
        </div>
      )}

      {loading ? (
        <div className="card text-muted text-[13px]">Caricamento…</div>
      ) : (
        <DataTable
          rows={users}
          columns={columns}
          rowKey={(u) => u.id}
          pageSize={25}
          empty={
            <div className="px-4 py-8 text-center text-muted text-[13px]">
              Nessun operatore. Promuovi un utente per iniziare.
            </div>
          }
        />
      )}

      {promoteOpen && (
        <PromoteModal
          onClose={() => setPromoteOpen(false)}
          onDone={(email) => {
            setPromoteOpen(false);
            showToast({
              kind: "success",
              message: `${email} promosso a operation.`,
            });
            load();
          }}
        />
      )}

      {demoting && (
        <DemoteModal
          user={demoting}
          onClose={() => setDemoting(null)}
          onDone={() => {
            setDemoting(null);
            showToast({
              kind: "success",
              message: `${demoting.email} riportato a user.`,
            });
            load();
          }}
        />
      )}
    </section>
  );
}

/** Modal di promozione: cerca l'utente per email tra quelli registrati e
 *  promuove a operation. Non crea nuovi utenti — la registrazione passa
 *  comunque dal flow standard. */
function PromoteModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (email: string) => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handlePromote() {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      // Lookup utente per email (filtro lato client su tutti gli utenti).
      // Non c'è un endpoint dedicato `/v1/admin/users/by-email`, quindi
      // ripieghiamo sulla lista paginata.
      const listRes = await authFetch("/v1/admin/users?limit=200");
      if (!listRes.ok) {
        throw new Error(`lookup ${listRes.status}`);
      }
      const list = (await listRes.json()) as UsersResponse;
      const target = (list.items ?? []).find(
        (u) => u.email.toLowerCase() === email.trim().toLowerCase(),
      );
      if (!target) {
        throw new Error(`Utente non trovato: ${email}`);
      }
      if (target.role === "operation" || target.role === "admin") {
        throw new Error(`${target.email} è già ${target.role}.`);
      }
      const res = await authFetch(`/v1/admin/users/${target.id}/role`, {
        method: "PUT",
        body: JSON.stringify({ role: "operation" }),
      });
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const body = await res.json();
          detail = body.detail ?? body.message ?? null;
        } catch {
          /* ignore */
        }
        throw new Error(detail ?? `Promozione fallita (${res.status})`);
      }
      onDone(target.email);
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "errore",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[420px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Promuovi a operation</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <p className="text-[12.5px] text-text-2 mb-3">
          L&apos;utente deve essere già registrato.
        </p>
        <label className="flex flex-col gap-1 mb-4">
          <span className="text-[11.5px] text-muted">Email</span>
          <input
            className="input"
            type="email"
            placeholder="user@quantica.local"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handlePromote}
            disabled={submitting || !email.trim()}
          >
            {submitting ? "Promozione…" : "Promuovi"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Modal di demozione — riporta un operation/admin allo stato user. */
function DemoteModal({
  user,
  onClose,
  onDone,
}: {
  user: UserWithRole;
  onClose: () => void;
  onDone: () => void;
}) {
  const showToast = useStore((s) => s.showToast);
  const [submitting, setSubmitting] = useState(false);

  async function handleDemote() {
    setSubmitting(true);
    try {
      const res = await authFetch(`/v1/admin/users/${user.id}/role`, {
        method: "PUT",
        body: JSON.stringify({ role: "user" }),
      });
      if (!res.ok) {
        let detail: string | null = null;
        try {
          const body = await res.json();
          detail = body.detail ?? body.message ?? null;
        } catch {
          /* ignore */
        }
        throw new Error(detail ?? `Demozione fallita (${res.status})`);
      }
      onDone();
    } catch (e) {
      showToast({
        kind: "error",
        message: e instanceof Error ? e.message : "errore",
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-[420px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[16px] font-semibold">Demota operatore</h2>
          <button
            type="button"
            className="text-muted hover:text-text text-[18px] leading-none"
            onClick={onClose}
            aria-label="Chiudi"
          >
            ×
          </button>
        </div>
        <p className="text-[13px] text-text-2 mb-2">
          Confermi di riportare a <span className="font-mono">user</span>?
        </p>
        <p className="text-[12.5px] text-muted mb-4">
          {user.email} perderà accesso al backoffice.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={handleDemote}
            disabled={submitting}
          >
            {submitting ? "Demozione…" : "Demota"}
          </button>
        </div>
      </div>
    </div>
  );
}
